import {
  FiClock as Clock, FiDollarSign as DollarSign, FiCheckCircle as CheckCircle,
  FiHash as Hash, FiLayers as Layers, FiActivity as Activity,
  FiCheckSquare as CheckSquare, FiAlertTriangle as AlertTriangle, FiXCircle as XCircle,
  FiAlertCircle as AlertCircle, FiInfo as Info,
} from 'react-icons/fi';

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

export const formatRunDuration = (startedAt: string | null | undefined, completedAt: string | null | undefined) => {
  if (!startedAt) return null;
  const start = new Date(startedAt);
  const end = completedAt ? new Date(completedAt) : new Date();
  const ms = end.getTime() - start.getTime();

  const seconds = Math.floor(ms / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);

  if (hours > 0) return `${hours}h ${minutes % 60}m`;
  if (minutes > 0) return `${minutes}m ${seconds % 60}s`;
  return `${seconds}s`;
};

export const formatScoringMethod = (method: string | null | undefined) => {
  const map: Record<string, string> = {
    'exact_match': 'Exact Match',
    'contains': 'Contains',
    'similarity': 'Similarity',
    'semantic_similarity': 'Semantic Similarity',
    'llm_judge': 'LLM Judge',
    'bedrock_evaluation': 'AWS Bedrock Evaluation',
    'bert_score': 'BERTScore',
    'rouge': 'ROUGE',
  };
  return map[method as string] || method || 'Not specified';
};

export const getProviderFromModel = (modelId: string | null | undefined) => {
  if (!modelId) return 'Unknown';
  if (modelId.includes('anthropic') || modelId.includes('claude')) return 'Bedrock';
  if (modelId.includes('gpt') || modelId.includes('openai')) return 'OpenAI';
  if (modelId.includes('amazon') || modelId.includes('titan')) return 'Amazon';
  if (modelId.includes('meta') || modelId.includes('llama')) return 'Meta';
  if (modelId.includes('cohere')) return 'Cohere';
  if (modelId.includes('mistral')) return 'Mistral';
  return 'Bedrock';
};

export const formatModelName = (modelId: string | null | undefined) => {
  if (!modelId) return 'Unknown Model';

  // Handle different model ID formats:
  // - "anthropic.claude-3-5-sonnet-20240620-v1:0" -> "Claude 3.5 Sonnet"
  // - "global.anthropic.claude-sonnet-4-20250514-v1:0" -> "Claude Sonnet 4"
  // - "amazon.titan-text-express-v1" -> "Titan Text Express"

  // Remove global. prefix if present
  let cleanId = modelId.replace(/^global\./, '');

  // Split by provider (first dot)
  const parts = cleanId.split('.');
  const modelPart = parts.length > 1 ? parts[1] : parts[0];

  // Remove version suffix (-v1:0, -v2:0, etc.) and date suffixes
  let name = modelPart
    .replace(/-v\d+:\d+$/, '')  // Remove -v1:0 suffix
    .replace(/-\d{8}$/, '')      // Remove -20240620 date suffix
    .replace(/:.*$/, '');        // Remove anything after colon

  // Format the name nicely
  // claude-3-5-sonnet -> Claude 3.5 Sonnet
  // claude-sonnet-4 -> Claude Sonnet 4
  // titan-text-express -> Titan Text Express
  name = name
    .split('-')
    .map((word) => {
      // Keep numbers as-is
      if (/^\d+$/.test(word)) return word;
      // Capitalize first letter
      return word.charAt(0).toUpperCase() + word.slice(1);
    })
    .join(' ')
    // Convert "3 5" to "3.5" for version numbers
    .replaceAll(/(\d)\s+(\d)/g, '$1.$2');

  return name || modelId;
};

// ============================================================================
// TYPES
// ============================================================================

export interface EvaluationRun {
  id: string;
  status: string;
  model_id?: string;
  model?: string;
  model_parameters?: string | Record<string, unknown>;
  quality_gate_passed?: boolean | null;
  quality_gate_details?: Record<string, { expected?: number; actual?: number; passed?: boolean }>;
  error_message?: string | null;
  bedrock_job_arn?: string | null;
  bedrock_job_status?: string | null;
  bedrock_last_polled_at?: string | null;
  bedrock_timeout_minutes?: number | null;
  scoring_method?: string;
  dataset_name?: string;
  prompt_name?: string;
  include_safety?: boolean;
  safety_passed?: boolean | null;
  started_at?: string | null;
  completed_at?: string | null;
  created_at?: string;
  avg_score?: number | null;
  pass_rate?: number | null;
  passed_count?: number | null;
  failed_count?: number | null;
  total_test_cases?: number | null;
  total_cost?: number | null;
  total_tokens?: number | null;
  score?: number | null;
  duration?: number | null;
  dataset?: string | null;
  timestamp?: string | null;
  retry_at?: string | null;
  retry_count?: number | null;
  throttle_reason?: string | null;
  project_id?: string | null;
  workload_id?: string | null;
}

// ============================================================================
// RUN DETAILS COMPONENTS
// ============================================================================

// Run Summary Card - Shows configuration and key metrics
export const RunDetailsCard = ({ run }: { run: EvaluationRun }) => {
  const duration = formatRunDuration(run.started_at, run.completed_at);
  const modelParams = typeof run.model_parameters === 'string'
    ? JSON.parse(run.model_parameters || '{}')
    : (run.model_parameters || {});

  const scoringMethod = modelParams.scoring_method || run.scoring_method;
  const passThreshold = modelParams.pass_threshold;

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4 mb-4">
      <h4 className="font-medium text-gray-900 dark:text-white mb-3 flex items-center gap-2">
        <Layers className="w-4 h-4 text-blue-500" />
        Run Configuration
      </h4>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
        {/* Model */}
        <div>
          <span className="text-gray-500 dark:text-gray-400 block mb-1">Model</span>
          <p className="font-medium text-gray-900 dark:text-white" title={run.model_id}>
            {formatModelName(run.model_id)}
          </p>
          <p className="text-xs text-gray-400">{getProviderFromModel(run.model_id)}</p>
        </div>

        {/* Scoring Method */}
        <div>
          <span className="text-gray-500 dark:text-gray-400 block mb-1">Scoring Method</span>
          <p className="font-medium text-gray-900 dark:text-white">
            {formatScoringMethod(scoringMethod)}
          </p>
          {passThreshold && (
            <p className="text-xs text-gray-400">Threshold: {(passThreshold * 100).toFixed(0)}%</p>
          )}
        </div>

        {/* Dataset */}
        <div>
          <span className="text-gray-500 dark:text-gray-400 block mb-1">Dataset</span>
          <p className="font-medium text-gray-900 dark:text-white">
            {run.dataset_name || 'Not specified'}
          </p>
          <p className="text-xs text-gray-400">{run.total_test_cases || 0} test cases</p>
        </div>

        {/* Prompt */}
        <div>
          <span className="text-gray-500 dark:text-gray-400 block mb-1">Prompt</span>
          <p className="font-medium text-gray-900 dark:text-white">
            {run.prompt_name || 'Direct input'}
          </p>
        </div>
      </div>

      {/* Metrics Row */}
      <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700 grid grid-cols-2 md:grid-cols-5 gap-4 text-sm">
        <div className="flex items-center gap-2">
          <Clock className="w-4 h-4 text-purple-500" />
          <div>
            <span className="text-gray-500 dark:text-gray-400 block text-xs">Duration</span>
            <span className="font-medium text-gray-900 dark:text-white">{duration || 'In progress...'}</span>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <DollarSign className="w-4 h-4 text-yellow-500" />
          <div>
            <span className="text-gray-500 dark:text-gray-400 block text-xs">Cost</span>
            <span className="font-medium text-gray-900 dark:text-white">${(run.total_cost || 0).toFixed(4)}</span>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <Hash className="w-4 h-4 text-blue-500" />
          <div>
            <span className="text-gray-500 dark:text-gray-400 block text-xs">Tokens</span>
            <span className="font-medium text-gray-900 dark:text-white">{(run.total_tokens || 0).toLocaleString()}</span>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <Clock className="w-4 h-4 text-gray-500" />
          <div>
            <span className="text-gray-500 dark:text-gray-400 block text-xs">Started</span>
            <span className="font-medium text-gray-900 dark:text-white">
              {run.started_at ? new Date(run.started_at).toLocaleTimeString() : 'N/A'}
            </span>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <CheckCircle className="w-4 h-4 text-green-500" />
          <div>
            <span className="text-gray-500 dark:text-gray-400 block text-xs">Completed</span>
            <span className="font-medium text-gray-900 dark:text-white">
              {run.completed_at ? new Date(run.completed_at).toLocaleTimeString() : 'N/A'}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

// Quality Gate Details - Shows why the gate passed or failed
export const QualityGateDetails = ({ run }: { run: EvaluationRun }) => {
  if (run.quality_gate_passed == null) return null;

  const modelParams = typeof run.model_parameters === 'string'
    ? JSON.parse(run.model_parameters || '{}')
    : (run.model_parameters || {});

  // Get quality gate details from API response (preferred) or fall back to model_parameters
  const gateDetails = run.quality_gate_details || {};

  // Use API-provided threshold or fall back to model_parameters, then default
  // Backend default is 0.8 for pass_rate and 0.7 for avg_score
  const passRateThreshold = gateDetails.pass_rate?.expected || modelParams.pass_threshold || 0.8;
  const avgScoreThreshold = gateDetails.avg_score?.expected || modelParams.pass_threshold || 0.7;

  // Get actual values from API response first, then from run fields
  const passRate = gateDetails.pass_rate?.actual ?? run.pass_rate ?? 0;
  const avgScore = gateDetails.avg_score?.actual ?? run.avg_score ?? 0;

  // Build criteria from API details if available
  const criteria = [];

  // Pass rate check
  if (gateDetails.pass_rate) {
    criteria.push({
      name: 'Pass Rate',
      required: `>= ${((gateDetails.pass_rate.expected ?? 0) * 100).toFixed(0)}%`,
      actual: `${((gateDetails.pass_rate.actual ?? passRate) * 100).toFixed(1)}%`,
      passed: gateDetails.pass_rate.passed,
    });
  } else {
    // Fallback when no API details
    criteria.push({
      name: 'Pass Rate',
      required: `>= ${(passRateThreshold * 100).toFixed(0)}%`,
      actual: `${(passRate * 100).toFixed(1)}%`,
      passed: passRate >= passRateThreshold,
    });
  }

  // Average score check
  if (gateDetails.avg_score) {
    criteria.push({
      name: 'Average Score',
      required: `>= ${((gateDetails.avg_score.expected ?? 0) * 100).toFixed(0)}%`,
      actual: `${((gateDetails.avg_score.actual ?? avgScore) * 100).toFixed(1)}%`,
      passed: gateDetails.avg_score.passed,
    });
  } else if (avgScore !== passRate) {
    // Only show avg score if different from pass rate and no API details
    criteria.push({
      name: 'Average Score',
      required: `>= ${(avgScoreThreshold * 100).toFixed(0)}%`,
      actual: `${(avgScore * 100).toFixed(1)}%`,
      passed: avgScore >= avgScoreThreshold,
    });
  }

  // Add latency check if present
  if (gateDetails.latency) {
    criteria.push({
      name: 'Average Latency',
      required: `<= ${gateDetails.latency.expected}ms`,
      actual: `${gateDetails.latency.actual?.toFixed(0) || 0}ms`,
      passed: gateDetails.latency.passed,
    });
  }

  // Add cost check if present
  if (gateDetails.cost) {
    criteria.push({
      name: 'Total Cost',
      required: `<= $${gateDetails.cost.expected?.toFixed(2)}`,
      actual: `$${gateDetails.cost.actual?.toFixed(2) || '0.00'}`,
      passed: gateDetails.cost.passed,
    });
  }

  return (
    <div className={`rounded-lg border p-4 mb-4 ${
      run.quality_gate_passed
        ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800'
        : 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800'
    }`}>
      <h4
        className={`font-medium mb-3 flex items-center gap-2 ${
          run.quality_gate_passed
            ? 'text-green-800 dark:text-green-200'
            : 'text-red-800 dark:text-red-200'
        }`}
        title={run.quality_gate_passed
          ? 'All quality gate criteria met - this evaluation is production-ready'
          : 'One or more criteria failed to meet the required threshold. Review the table below for details'
        }
      >
        {run.quality_gate_passed ? <CheckSquare className="w-4 h-4" /> : <AlertTriangle className="w-4 h-4" />}
        Quality Gate {run.quality_gate_passed ? 'Passed' : 'Failed'}
      </h4>

      <table className="w-full text-sm">
        <thead>
          <tr className={`text-left ${
            run.quality_gate_passed
              ? 'text-green-600 dark:text-green-400'
              : 'text-red-600 dark:text-red-400'
          }`}>
            <th className="pb-2 font-medium">Criterion</th>
            <th className="pb-2 font-medium">Required</th>
            <th className="pb-2 font-medium">Actual</th>
            <th className="pb-2 font-medium text-right">Status</th>
          </tr>
        </thead>
        <tbody className={`${
          run.quality_gate_passed
            ? 'text-green-700 dark:text-green-300'
            : 'text-red-700 dark:text-red-300'
        }`}>
          {criteria.map((c) => (
            <tr key={c.name}>
              <td className="py-1">{c.name}</td>
              <td className="py-1">{c.required}</td>
              <td className="py-1 font-mono">{c.actual}</td>
              <td className="py-1 text-right">
                {c.passed ? (
                  <CheckCircle className="w-4 h-4 inline text-green-600" />
                ) : (
                  <XCircle className="w-4 h-4 inline text-red-600" />
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

// Bedrock Metrics Display - Visual progress bars for Bedrock evaluation metrics
export const BedrockMetricsDisplay = ({ run }: { run: EvaluationRun }) => {
  const modelParams = typeof run.model_parameters === 'string'
    ? JSON.parse(run.model_parameters || '{}')
    : (run.model_parameters || {});

  const bedrockMetrics = modelParams.bedrock_metrics;
  const overallScore = modelParams.bedrock_overall_score || run.avg_score;

  if (!bedrockMetrics || Object.keys(bedrockMetrics).length === 0) return null;

  const MetricBar = ({ name, value, inverted = false }: { name: string; value: number; inverted?: boolean }) => {
    // Normalize value to 0-100 for display
    let displayValue = value;
    let normalizedForBar = value;

    // Robustness can be 0-100
    if (name.includes('Robustness') && value > 1) {
      displayValue = value;
      normalizedForBar = value;
    } else {
      // Accuracy and Toxicity are 0-1
      displayValue = value * 100;
      normalizedForBar = value * 100;
    }

    // For toxicity, we show the actual value but color it inversely
    let barColor: string;
    if (inverted) {
      if (displayValue < 10) barColor = 'bg-green-500';
      else if (displayValue < 30) barColor = 'bg-yellow-500';
      else barColor = 'bg-red-500';
    } else {
      if (displayValue >= 70) barColor = 'bg-green-500';
      else if (displayValue >= 50) barColor = 'bg-yellow-500';
      else barColor = 'bg-red-500';
    }

    return (
      <div className="space-y-1">
        <div className="flex justify-between text-sm">
          <span className="text-gray-600 dark:text-gray-400">
            {name.replace('Builtin.', '')}
            {inverted && <span className="text-xs ml-1">(lower is better)</span>}
          </span>
          <span className="font-medium text-gray-900 dark:text-white">
            {displayValue.toFixed(1)}%
          </span>
        </div>
        <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
          <div
            className={`h-2 rounded-full ${barColor} transition-all duration-300`}
            style={{ width: `${Math.min(normalizedForBar, 100)}%` }}
          />
        </div>
      </div>
    );
  };

  return (
    <div className="bg-orange-50 dark:bg-orange-900/20 rounded-lg border border-orange-200 dark:border-orange-800 p-4 mb-4">
      <h4 className="font-medium text-orange-800 dark:text-orange-200 mb-3 flex items-center gap-2">
        <Activity className="w-4 h-4" />
        Bedrock Evaluation Metrics
      </h4>

      <div className="space-y-3">
        {(Object.entries(bedrockMetrics) as [string, number][]).map(([name, value]) => (
          <MetricBar
            key={name}
            name={name}
            value={value}
            inverted={name.includes('Toxicity')}
          />
        ))}
      </div>

      {/* Overall Score */}
      <div className="mt-4 pt-3 border-t border-orange-200 dark:border-orange-700">
        <div className="flex justify-between items-center">
          <span className="font-medium text-orange-800 dark:text-orange-200">Overall Score</span>
          <span className="text-2xl font-bold text-orange-900 dark:text-orange-100">
            {((overallScore || 0) * 100).toFixed(1)}%
          </span>
        </div>
      </div>
    </div>
  );
};

// Bedrock Results Summary - Compact inline display for Results tab when no individual test case results
export const BedrockResultsSummary = ({ run }: { run: EvaluationRun }) => {
  const modelParams = typeof run.model_parameters === 'string'
    ? JSON.parse(run.model_parameters || '{}')
    : (run.model_parameters || {});

  const bedrockMetrics = modelParams.bedrock_metrics;
  const overallScore = modelParams.bedrock_overall_score || run.avg_score;

  if (!bedrockMetrics || Object.keys(bedrockMetrics).length === 0) return null;

  const formatMetricValue = (name: string, value: number) => {
    if (name.includes('Robustness') && value > 1) {
      return `${value.toFixed(1)}%`;
    }
    return `${(value * 100).toFixed(1)}%`;
  };

  const getMetricColor = (name: string, value: number) => {
    const displayValue = name.includes('Robustness') && value > 1 ? value : value * 100;
    if (name.includes('Toxicity')) {
      if (displayValue < 10) return 'text-green-600';
      if (displayValue < 30) return 'text-yellow-600';
      return 'text-red-600';
    }
    if (displayValue >= 70) return 'text-green-600';
    if (displayValue >= 50) return 'text-yellow-600';
    return 'text-red-600';
  };

  return (
    <div className="py-4">
      <div className="bg-orange-50 dark:bg-orange-900/20 rounded-lg border border-orange-200 dark:border-orange-800 p-4">
        <div className="flex items-center gap-2 mb-3">
          <Activity className="w-4 h-4 text-orange-600" />
          <span className="text-sm font-medium text-orange-800 dark:text-orange-200">
            Bedrock Evaluation Results
          </span>
          <span className="text-xs text-orange-600 dark:text-orange-400">
            (Aggregate metrics - no individual test case results)
          </span>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-orange-200 dark:border-orange-700">
                <th className="text-left py-2 px-3 font-medium text-orange-800 dark:text-orange-200">Metric</th>
                <th className="text-right py-2 px-3 font-medium text-orange-800 dark:text-orange-200">Score</th>
              </tr>
            </thead>
            <tbody>
              {(Object.entries(bedrockMetrics) as [string, number][]).map(([name, value]) => (
                <tr key={name} className="border-b border-orange-100 dark:border-orange-800/50">
                  <td className="py-2 px-3 text-gray-700 dark:text-gray-300">
                    {name.replace('Builtin.', '')}
                    {name.includes('Toxicity') && (
                      <span className="text-xs text-gray-500 ml-1">(lower is better)</span>
                    )}
                  </td>
                  <td className={`py-2 px-3 text-right font-medium ${getMetricColor(name, value)}`}>
                    {formatMetricValue(name, value)}
                  </td>
                </tr>
              ))}
              <tr className="bg-orange-100/50 dark:bg-orange-800/30">
                <td className="py-2 px-3 font-medium text-orange-900 dark:text-orange-100">Overall Score</td>
                <td className="py-2 px-3 text-right font-bold text-orange-900 dark:text-orange-100">
                  {((overallScore || 0) * 100).toFixed(1)}%
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

// Error Details Card - Shows error with context and suggestions
export const ErrorDetailsCard = ({ run }: { run: EvaluationRun }) => {
  if (!run.error_message && run.status !== 'failed') return null;

  // Parse known error patterns and provide solutions
  const getErrorSolution = (error: string | null | undefined) => {
    if (!error) return null;

    // Match global.* model errors - Bedrock returns 'modelIdentifier' (camelCase)
    if (error.includes('global.') && (error.toLowerCase().includes('modelidentifier') || error.toLowerCase().includes('model identifier') || error.includes('inferenceConfig.models'))) {
      return {
        title: 'Invalid Model for Bedrock Evaluations',
        solution: 'Cross-region inference profiles (global.*) are not supported for Bedrock Evaluations. Use a regional model instead.',
        example: 'Example: anthropic.claude-3-5-sonnet-20240620-v1:0',
      };
    }

    if (error.includes('AccessDenied') || error.includes('not authorized')) {
      return {
        title: 'AWS Permission Error',
        solution: 'Your AWS credentials may not have the required permissions for Bedrock Evaluations.',
        action: 'Check your IAM policy includes bedrock:CreateEvaluationJob and bedrock:GetEvaluationJob permissions.',
      };
    }

    if (error.includes('timeout') || error.includes('timed out')) {
      return {
        title: 'Evaluation Timed Out',
        solution: 'The evaluation took longer than the configured timeout. Try reducing the number of test cases or increasing the timeout.',
      };
    }

    if (error.includes('rate limit') || error.includes('throttl')) {
      return {
        title: 'Rate Limit Exceeded',
        solution: 'AWS Bedrock rate limits were exceeded. Wait a few minutes and try again with fewer concurrent requests.',
      };
    }

    return null;
  };

  const solution = getErrorSolution(run.error_message);

  return (
    <div className="bg-red-50 dark:bg-red-900/20 rounded-lg border border-red-200 dark:border-red-800 p-4 mb-4">
      <h4 className="font-medium text-red-800 dark:text-red-200 mb-2 flex items-center gap-2">
        <AlertCircle className="w-4 h-4" />
        {solution?.title || 'Evaluation Failed'}
      </h4>

      {/* Error message */}
      <div className="mb-3 p-3 bg-red-100 dark:bg-red-900/40 rounded text-sm font-mono text-red-800 dark:text-red-200 break-words">
        {run.error_message || 'Unknown error occurred'}
      </div>

      {/* Solution */}
      {solution && (
        <div className="space-y-2 text-sm text-red-700 dark:text-red-300">
          <p className="flex items-start gap-2">
            <Info className="w-4 h-4 mt-0.5 flex-shrink-0" />
            <span>{solution.solution}</span>
          </p>
          {solution.example && (
            <p className="ml-6 font-mono text-xs bg-red-100 dark:bg-red-900/40 px-2 py-1 rounded inline-block">
              {solution.example}
            </p>
          )}
          {solution.action && (
            <p className="ml-6 text-xs opacity-80">{solution.action}</p>
          )}
        </div>
      )}
    </div>
  );
};
